/**
 * Protokolle — Liste + Wizard.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, ClipboardText, Download, Eye, PencilSimple, Trash, ShieldCheck } from "@phosphor-icons/react";
import ProtocolWizard from "@/components/ProtocolWizard";

const TYPE_LABELS = {
  handover_in: "Übergabe (Einzug)",
  handover_out: "Rücknahme (Auszug)",
  key_handover: "Schlüsselübergabe",
  inspection: "Begehung",
  document_handover: "Dokumentenübergabe",
  interim_inspection: "Zwischenabnahme",
  trade_acceptance: "Baugewerke-Abnahme",
};

const STATUS_LABEL = { draft: "Entwurf", signed: "Signiert", archived: "Archiv" };
const STATUS_COLOR = {
  draft: "bg-amber-50 text-amber-800 border-amber-200",
  signed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  archived: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function Protocols() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ q: "", type: "", status: "" });
  const [openWizard, setOpenWizard] = useState(false);
  const [wizardInitial, setWizardInitial] = useState({});

  const load = async () => {
    try {
      const params = {};
      if (filter.type) params.protocol_type = filter.type;
      if (filter.status) params.status = filter.status;
      const { data } = await api.get("/protocols", { params });
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [filter.type, filter.status]);

  const openNew = () => { setWizardInitial({}); setOpenWizard(true); };
  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`/protocols/${id}`);
      setWizardInitial(data);
      setOpenWizard(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const downloadPdf = async (id, signed) => {
    const url = `/protocols/${id}/${signed ? "download-pdf" : "preview-pdf"}`;
    const { data } = await api.get(url, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(data);
    window.open(blobUrl, "_blank");
  };
  const del = async (id) => {
    if (!await window._domivusConfirm("Entwurf wirklich löschen?")) return;
    try { await api.delete(`/protocols/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = (items || []).filter((p) => {
    if (!filter.q) return true;
    const s = filter.q.toLowerCase();
    return (
      (p.location || "").toLowerCase().includes(s) ||
      (p.tenancy_id || "").includes(s) ||
      (TYPE_LABELS[p.protocol_type] || "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="protocols-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <ClipboardText size={24} className="text-emerald-600" /> Protokolle
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Übergabe, Rücknahme, Schlüssel, Begehung, Dokumentenübergabe — mit Wizard und
            digitaler Unterschrift oder Ausdruck.
          </p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2" onClick={openNew}
                data-testid="protocols-new-btn">
          <Plus size={16} /> Neues Protokoll
        </Button>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap gap-2 items-center">
        <Input className="max-w-xs" placeholder="Suchen …" value={filter.q}
               onChange={(e) => setFilter((s) => ({ ...s, q: e.target.value }))}
               data-testid="protocols-search" />
        <select className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                value={filter.type} onChange={(e) => setFilter((s) => ({ ...s, type: e.target.value }))}
                data-testid="protocols-filter-type">
          <option value="">Alle Typen</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                value={filter.status} onChange={(e) => setFilter((s) => ({ ...s, status: e.target.value }))}
                data-testid="protocols-filter-status">
          <option value="">Alle Status</option>
          <option value="draft">Entwurf</option>
          <option value="signed">Signiert</option>
          <option value="archived">Archiviert</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Typ</th>
              <th className="text-left px-3 py-2">Termin</th>
              <th className="text-left px-3 py-2">Ort</th>
              <th className="text-left px-3 py-2">Beteiligte</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2 pr-4">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center px-3 py-10 text-slate-400 italic">Keine Protokolle vorhanden.</td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`protocol-row-${p.id}`}>
                <td className="px-3 py-2 font-medium">{TYPE_LABELS[p.protocol_type] || p.protocol_type}</td>
                <td className="px-3 py-2 tabular-nums text-slate-600">
                  {p.appointment_date ? formatDate(p.appointment_date) : "—"}{p.appointment_time ? ` ${p.appointment_time}` : ""}
                </td>
                <td className="px-3 py-2 text-slate-600">{p.location || "—"}</td>
                <td className="px-3 py-2 text-slate-600">
                  {(p.participants || []).map((x) => x.name).filter(Boolean).slice(0, 3).join(", ") || "—"}
                </td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center gap-1 h-6 px-2 rounded-full border text-[11px] ${STATUS_COLOR[p.status]}`}>
                    {p.status === "signed" && <ShieldCheck size={11} />}
                    {STATUS_LABEL[p.status] || p.status}
                  </span>
                </td>
                <td className="px-3 py-2 pr-4 text-right space-x-1">
                  <Button variant="ghost" size="icon" onClick={() => downloadPdf(p.id, false)}
                          title="Vorschau anzeigen" data-testid={`protocol-preview-${p.id}`}>
                    <Eye size={14} />
                  </Button>
                  {p.status === "signed" && (
                    <Button variant="ghost" size="icon" onClick={() => downloadPdf(p.id, true)}
                            title="Signierte PDF herunterladen" data-testid={`protocol-download-${p.id}`}>
                      <Download size={14} />
                    </Button>
                  )}
                  {p.status !== "signed" && (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(p.id)}
                              title="Bearbeiten" data-testid={`protocol-edit-${p.id}`}><PencilSimple size={14} /></Button>
                      <Button variant="ghost" size="icon" onClick={() => del(p.id)}
                              title="Löschen" data-testid={`protocol-delete-${p.id}`}><Trash size={14} /></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ProtocolWizard open={openWizard} onOpenChange={setOpenWizard} initial={wizardInitial} onSaved={load} />
    </div>
  );
}
